import { useSortable, type UseSortableOptions } from "@vueuse/integrations/useSortable"
import type { MoveEvent } from "sortablejs"
import { nextTick, toValue, watchPostEffect, type MaybeRef, type Ref } from "vue"

export interface SortableListEvent {
  item: HTMLElement
  from: HTMLElement
  to: HTMLElement
  oldIndex?: number
  newIndex?: number
  oldDraggableIndex?: number
  newDraggableIndex?: number
}

export interface SortableListChange<T> {
  added?: { element: T; newIndex: number }
  removed?: { element: T; oldIndex: number }
  moved?: { element: T; oldIndex: number; newIndex: number }
}

export interface SortableListMove<T> {
  draggedContext: { element: T; index: number; futureIndex: number }
  relatedContext: { element?: T; index: number; list: T[] }
}

interface ListOptions<T> extends Omit<
  UseSortableOptions,
  "onUpdate" | "onAdd" | "onRemove" | "onMove" | "onStart" | "onEnd" | "onChange"
> {
  onStart?: (event: SortableListEvent) => void
  onEnd?: (event: SortableListEvent) => void
  onChange?: (change: SortableListChange<T>) => void
  onMove?: (event: SortableListMove<T>) => boolean | void
}

const lists = new WeakMap<
  HTMLElement,
  {
    items: () => unknown[]
    indices: WeakMap<Element, number>
  }
>()
const drags = new WeakMap<HTMLElement, { element: unknown; index: number }>()

/** Keep Vue in charge of keyed nodes, including transfers between sortable lists. */
export function useSortableList<T>(
  target: Ref<HTMLElement | null | undefined>,
  list: MaybeRef<T[]>,
  options: ListOptions<T> = {}
) {
  const { onChange, onMove, onStart, onEnd, ...sortableOptions } = options
  watchPostEffect((onCleanup) => {
    const element = target.value
    // Track the rendered order before Sortable temporarily moves nodes around.
    const items = [...toValue(list)]
    if (!element) return
    const indices = new WeakMap<Element, number>()
    const children = Array.from(element.children).filter(
      (child) =>
        !sortableOptions.draggable || child.matches(sortableOptions.draggable.replace(/^>\s*/, ""))
    )
    children.slice(0, items.length).forEach((child, index) => indices.set(child, index))
    lists.set(element, { items: () => toValue(list), indices })
    onCleanup(() => lists.delete(element))
  })
  const notify = (change: SortableListChange<T>) => {
    if (onChange) nextTick(() => onChange(change))
  }
  const restore = (event: SortableListEvent) => {
    const siblings = Array.from(event.from.children).filter((child) => child !== event.item)
    event.from.insertBefore(event.item, siblings[event.oldIndex ?? 0] ?? null)
  }
  const context = (event: SortableListEvent) => {
    const index = event.oldDraggableIndex ?? event.oldIndex
    return (
      drags.get(event.item) ??
      (index === undefined ? undefined : { element: toValue(list)[index], index })
    )
  }

  return useSortable(target, list, {
    ...sortableOptions,
    watchElement: true,
    onStart(event: SortableListEvent) {
      const drag = context(event)
      if (drag) drags.set(event.item, drag)
      onStart?.(event)
    },
    onUpdate(event: SortableListEvent) {
      restore(event)
      const drag = context(event)
      const newIndex = event.newDraggableIndex ?? event.newIndex
      if (!drag || newIndex === undefined || drag.index === newIndex) return
      const items = toValue(list)
      const [element] = items.splice(drag.index, 1)
      items.splice(newIndex, 0, element)
      notify({ moved: { element, oldIndex: drag.index, newIndex } })
    },
    onAdd(event: SortableListEvent) {
      const drag = drags.get(event.item)
      const newIndex = event.newDraggableIndex ?? event.newIndex
      if (!drag || newIndex === undefined) return
      event.item.remove()
      const element = drag.element as T
      toValue(list).splice(newIndex, 0, element)
      notify({ added: { element, newIndex } })
    },
    onRemove(event: SortableListEvent) {
      restore(event)
      const drag = context(event)
      if (!drag) return
      const [element] = toValue(list).splice(drag.index, 1)
      notify({ removed: { element, oldIndex: drag.index } })
    },
    onMove(event: MoveEvent) {
      if (!onMove) return true
      const drag = drags.get(event.dragged)
      if (!drag) return true
      // The destination may not have been dragged from yet.
      const destination = lists.get(event.to)
      const relatedList = (destination?.items() ?? []) as T[]
      const children = Array.from(event.to.children).filter(
        (child) => (child as HTMLElement).style.display !== "none"
      )
      const index = destination?.indices.get(event.related) ?? -1
      const futureIndex =
        index < 0 ? 0 : index + (children.includes(event.dragged) || !event.willInsertAfter ? 0 : 1)
      return onMove({
        draggedContext: { element: drag.element as T, index: drag.index, futureIndex },
        relatedContext: { element: relatedList[index], index, list: relatedList }
      })
    },
    onEnd(event: SortableListEvent) {
      drags.delete(event.item)
      onEnd?.(event)
    }
  })
}
